import { tailnetIdentityOf } from '@featit/shared/tailnet-identities';
import { installTailnetTransport } from '@/services/tailnetTransport';

/**
 * F-0821 (NF-9, NF-10): this Mac answers on one of two tailnet names, and a passkey belongs to the one
 * the deployment verifies at (`passkeyOrigin.ts`). A page on the other name must not be moved to the
 * passkey's name when that name is the one that does not answer: the person would land on a dead page,
 * which reads as an outage, when nothing is wrong but the active Tailscale login.
 *
 * What is honestly possible is to say it: the credential is not wrong, nothing is unreachable, the
 * passkey is tied to an address that is off with this login, and a password works here.
 */
export function passkeyAtOtherTailnetNameSentence(
  ceremonyOrigin: string,
  hereHost: string,
  ceremonyOriginAnswers: boolean,
): string | null {
  const ceremonyHost = tailnetIdentityOf(ceremonyOrigin);
  const here = tailnetIdentityOf(`https://${hereHost}`);
  if (ceremonyHost === null || here === null || ceremonyHost === here || ceremonyOriginAnswers) {
    return null;
  }
  return (
    `Your passkey is tied to ${ceremonyHost}, which is switched off while this Mac's other Tailscale ` +
    `login is active, so this page (${here}) cannot use it. Your passkey is fine. Sign in with your ` +
    `email and password here, or switch Tailscale back to use the passkey.`
  );
}

/** The sentence for a passkey moved off this page, or null when the move to its address should go ahead. */
export async function passkeyAtOtherTailnetName(
  ceremonyOrigin: string,
  hereHost: string = window.location.hostname,
): Promise<string | null> {
  const ceremonyHost = tailnetIdentityOf(ceremonyOrigin);
  if (ceremonyHost === null || tailnetIdentityOf(`https://${hereHost}`) === null) return null;
  const resolver = installTailnetTransport();
  const answered = resolver ? tailnetIdentityOf(await resolver.resolve(ceremonyOrigin)) : null;
  return passkeyAtOtherTailnetNameSentence(ceremonyOrigin, hereHost, answered === ceremonyHost);
}
